import { useEffect, useState, useMemo } from 'react';
import { adminApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Modal from '../../components/ui/Modal.jsx';
import Select from '../../components/ui/Select.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { Plane, UploadCloud, Download, Trash2, Save, GitBranch, RotateCcw } from 'lucide-react';
import { visaTypeLabel, VISA_STATUS_VARIANT, VISA_STATUS_LABEL } from '../../lib/visas.js';

export default function AdminVisaRequestsPage() {
  const toast = useToast();
  const [visas, setVisas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(null); // fulfil/edit form
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Approval-chain config popup
  const [chains, setChains] = useState([]);
  const [visaChainId, setVisaChainId] = useState('');
  const [showChainConfig, setShowChainConfig] = useState(false);
  const [chainDraft, setChainDraft] = useState('');
  const [savingChain, setSavingChain] = useState(false);

  const load = () => {
    setLoading(true);
    return adminApi.listVisas()
      .then((res) => setVisas(res.visas || []))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load the configurable chains + the currently-selected visa chain so the
  // top-right config popup can show/change it.
  useEffect(() => {
    adminApi.listChains().then((res) => setChains((res.chains || []).filter((c) => c.isActive !== false))).catch(() => {});
    adminApi.getSettings().then((res) => setVisaChainId(res.settings?.visaApprovalChainId || '')).catch(() => {});
  }, []);

  const openChainConfig = () => { setChainDraft(visaChainId); setShowChainConfig(true); };
  const saveChainConfig = async () => {
    setSavingChain(true);
    try {
      await adminApi.updateSettings({ visaApprovalChainId: chainDraft });
      setVisaChainId(chainDraft);
      setShowChainConfig(false);
      toast.success('Visa approval chain updated');
    } catch (err) { toast.error(err.message); } finally { setSavingChain(false); }
  };
  const currentChainName = visaChainId
    ? (chains.find((c) => c.rowKey === visaChainId)?.name || visaChainId)
    : 'Direct manager (default)';

  useEffect(() => {
    if (selected) {
      setForm({
        visaNumber: selected.visaNumber || '',
        startDate: selected.startDate || '',
        endDate: selected.endDate || '',
        reminderDays: selected.reminderDays ?? 30,
        status: selected.status,
      });
      setFile(null);
      setConfirmDelete(false);
    } else { setForm(null); }
  }, [selected]);

  const filtered = useMemo(
    () => (statusFilter === 'all' ? visas : visas.filter((v) => v.status === statusFilter)),
    [visas, statusFilter]
  );

  const handleFulfill = async () => {
    if (!form.endDate) { toast.error('Enter the visa expiry (end date)'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      if (file) fd.append('file', file);
      fd.append('visaNumber', form.visaNumber || '');
      fd.append('startDate', form.startDate || '');
      fd.append('endDate', form.endDate || '');
      await adminApi.fulfillVisa(selected.partitionKey, selected.rowKey, fd);
      toast.success('Visa issued — employee notified');
      setSelected(null);
      await load();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const handleSaveEdit = async () => {
    setBusy(true);
    try {
      await adminApi.updateVisa(selected.partitionKey, selected.rowKey, {
        visaNumber: form.visaNumber,
        startDate: form.startDate,
        endDate: form.endDate,
        reminderDays: form.reminderDays,
        status: form.status,
      });
      toast.success('Updated');
      setSelected(null);
      await load();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await adminApi.deleteVisa(selected.partitionKey, selected.rowKey);
      toast.success('Deleted');
      setSelected(null);
      await load();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  // Restart the chain. resnapshot=true rebuilds from the currently-configured
  // visa chain (re-routes an in-flight request onto a new chain); otherwise
  // resets the existing chain's steps from `fromStep`.
  const handleRestartChain = async ({ resnapshot, fromStep } = {}) => {
    if (resnapshot && !confirm('Rebuild this request onto the currently-configured visa chain? It goes back to pending and starts from the first approver of that chain.')) return;
    setBusy(true);
    try {
      await adminApi.restartVisaChain(selected.partitionKey, selected.rowKey, resnapshot ? { resnapshot: true } : { fromStep });
      toast.success(resnapshot ? 'Moved onto the current chain' : `Restarted from step ${(fromStep || 0) + 1}`);
      setSelected(null);
      await load();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const columns = [
    { key: 'requesterName', label: 'Employee', render: (r) => (<div><div className="font-medium">{r.requesterName}</div><div className="text-xs text-muted-foreground">{r.requesterEmail}</div></div>) },
    { key: 'visaType', label: 'Type', render: (r) => visaTypeLabel(r.visaType) },
    { key: 'endDate', label: 'Valid until', render: (r) => r.endDate || '—' },
    { key: 'status', label: 'Status', render: (r) => <Badge variant={VISA_STATUS_VARIANT[r.status] || 'default'}>{VISA_STATUS_LABEL[r.status] || r.status}</Badge> },
  ];

  const STATUSES = ['all', 'pending', 'approved', 'fulfilled', 'rejected', 'cancelled', 'expired'];

  return (
    <div>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Plane className="h-6 w-6 text-primary" /> Visa Requests</h1>
          <p className="text-muted-foreground">Approve, then issue visas (upload + validity dates). Employees get expiry reminders.</p>
        </div>
        <Button variant="secondary" onClick={openChainConfig}>
          <GitBranch className="h-4 w-4" /> Approval chain
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium capitalize ${statusFilter === s ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-muted-foreground hover:bg-accent'}`}>
            {s}
          </button>
        ))}
      </div>

      <DataTable columns={columns} rows={filtered} loading={loading} emptyMessage="No visa requests." keyFn={(r) => `${r.partitionKey}-${r.rowKey}`} onRowClick={(r) => setSelected(r)} />

      {selected && form && (
        <Modal isOpen onClose={() => setSelected(null)} title={`${visaTypeLabel(selected.visaType)} — ${selected.requesterName}`} width="min(620px, 96vw)">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><div className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">Status</div><Badge variant={VISA_STATUS_VARIANT[selected.status] || 'default'}>{VISA_STATUS_LABEL[selected.status] || selected.status}</Badge></div>
              <div><div className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">Purpose</div>{selected.purpose || '—'}</div>
              <div><div className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">Needed by</div>{selected.neededBy || '—'}</div>
            </div>

            {Array.isArray(selected.chainSnapshot) && selected.chainSnapshot.length > 0 && (
              <div className="rounded-lg border border-border p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Approval Chain</div>
                  <Button variant="secondary" size="sm" onClick={() => handleRestartChain({ resnapshot: true })} disabled={busy}
                    title="Rebuild onto the currently-configured visa chain">
                    <RotateCcw className="h-3.5 w-3.5" /> Move to current chain
                  </Button>
                </div>
                <div className="space-y-2">
                  {selected.chainSnapshot.map((step, i) => {
                    const isDecided = step.status === 'approved' || step.status === 'rejected';
                    return (
                      <div key={i} className="flex items-center justify-between gap-2 text-xs rounded-md bg-muted/30 px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-mono text-muted-foreground">{i + 1}.</span>
                          <span className="truncate">{step.label || step.email || step.value}</span>
                          {step.status === 'approved' && <Badge variant="success">Approved</Badge>}
                          {step.status === 'rejected' && <Badge variant="danger">Rejected</Badge>}
                          {step.status === 'pending' && <Badge variant="warning">Pending</Badge>}
                          {isDecided && step.decidedBy && (
                            <span className="truncate text-muted-foreground">
                              — {step.decidedBy}{step.decidedAt ? ` · ${new Date(step.decidedAt).toLocaleString()}` : ''}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => handleRestartChain({ fromStep: i })}
                          disabled={busy}
                          className="flex-shrink-0 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-background hover:border-primary/40 transition-colors cursor-pointer disabled:opacity-50"
                          title={`Restart chain from step ${i + 1}`}
                        >
                          Restart from here
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {(selected.status === 'approved' || selected.status === 'fulfilled') ? (
              <div className="rounded-lg border border-border p-4 space-y-3">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {selected.status === 'approved' ? 'Issue the visa' : 'Issued visa'}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input label="Visa number" value={form.visaNumber} onChange={(e) => setForm({ ...form, visaNumber: e.target.value })} />
                  <Input label="Reminder (days before)" type="number" min="1" value={form.reminderDays} onChange={(e) => setForm({ ...form, reminderDays: e.target.value })} />
                  <Input label="Valid from" type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
                  <Input label="Valid until (expiry)" type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
                </div>
                <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-3 py-3 text-sm text-muted-foreground hover:border-primary/50">
                  <UploadCloud className="h-4 w-4" />
                  {file ? file.name : (selected.fileName ? `Replace file (current: ${selected.fileName})` : 'Upload the issued visa (PDF/image)')}
                  <input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                </label>
                {selected.visaUrl && (
                  <a href={selected.visaUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-medium text-primary"><Download className="h-3.5 w-3.5" /> View current file</a>
                )}
                <div className="flex justify-between items-center pt-1">
                  {!confirmDelete ? (
                    <Button variant="ghost" className="text-destructive hover:bg-destructive/10" onClick={() => setConfirmDelete(true)} disabled={busy}><Trash2 className="h-4 w-4" /> Delete</Button>
                  ) : (
                    <div className="flex items-center gap-2"><span className="text-xs text-destructive">Delete?</span><Button variant="danger" onClick={handleDelete} loading={busy}>Yes</Button><Button variant="ghost" onClick={() => setConfirmDelete(false)} disabled={busy}>No</Button></div>
                  )}
                  <div className="flex gap-2">
                    {selected.status === 'fulfilled' && <Button variant="secondary" onClick={handleSaveEdit} loading={busy} disabled={busy}><Save className="h-4 w-4" /> Save</Button>}
                    <Button onClick={handleFulfill} loading={busy} disabled={busy}><UploadCloud className="h-4 w-4" /> {selected.status === 'approved' ? 'Issue visa' : 'Re-issue'}</Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-border p-4 text-sm text-muted-foreground">
                This request is <strong className="text-foreground">{VISA_STATUS_LABEL[selected.status] || selected.status}</strong>. You can issue it once it's fully approved.
                <div className="flex justify-end mt-3">
                  {!confirmDelete ? (
                    <Button variant="ghost" className="text-destructive hover:bg-destructive/10" onClick={() => setConfirmDelete(true)} disabled={busy}><Trash2 className="h-4 w-4" /> Delete</Button>
                  ) : (
                    <div className="flex items-center gap-2"><span className="text-xs text-destructive">Delete?</span><Button variant="danger" onClick={handleDelete} loading={busy}>Yes</Button><Button variant="ghost" onClick={() => setConfirmDelete(false)} disabled={busy}>No</Button></div>
                  )}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {showChainConfig && (
        <Modal isOpen onClose={() => setShowChainConfig(false)} title="Visa approval chain" width="min(520px, 96vw)">
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Choose which approval chain new visa requests follow. Currently: <strong className="text-foreground">{currentChainName}</strong>.
            </p>
            <Select
              label="Approval chain"
              value={chainDraft}
              onChange={(e) => setChainDraft(e.target.value)}
              options={[
                { value: '', label: 'Direct manager (default)' },
                ...chains.map((c) => ({ value: c.rowKey, label: c.name || c.rowKey })),
              ]}
            />
            <p className="text-xs text-muted-foreground">
              Leave as “Direct manager” to route each request to the requester's own manager. Manage chains in the Approval Chains page.
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={() => setShowChainConfig(false)} disabled={savingChain}>Cancel</Button>
              <Button onClick={saveChainConfig} loading={savingChain} disabled={savingChain}><Save className="h-4 w-4" /> Save</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
